import { queueApiRequest, REQUEST_PRIORITY } from './ApiRequestManager'
import { handleAuthExpired, handleSessionReplaced } from '../utils/sessionReplacedHandler'
import { getTenantId as getResolvedTenantId } from '../utils/tenantUtils'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

interface ApiRequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
    headers?: Record<string, string>
    body?: any
    timeout?: number
}

const isAuthExpiredResponse = (status: number, responseData: any): boolean => {
    if (status === 401) return true
    if (status !== 403) return false
    const message = String(responseData?.error || responseData?.message || '').toLowerCase()
    return message.includes('invalid or expired token') || message.includes('invalid token') || message.includes('expired token')
}

const shouldHandleAuthExpired = (endpoint: string): boolean => {
    // Login can legitimately return 401 for invalid credentials; don't show "session expired" there.
    if (endpoint.includes('/api/v1/user-auth/login')) {
        return false
    }

    return true
}

/**
 * Make API request with rate limiting and error handling
 */
export async function apiRequest<T>(
    endpoint: string,
    options: ApiRequestOptions = {}
): Promise<T> {
    const {
        method = 'GET',
        headers = {},
        body,
        timeout = 30000
    } = options

    // Get base URL from environment or use default
    const url = withApiBaseUrl(endpoint)

    // Get access token if available
    const accessToken = localStorage.getItem('accessToken')

    // Prepare headers
    const requestHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...headers
    }

    // Add authorization header if token exists
    if (accessToken) {
        requestHeaders['Authorization'] = `Bearer ${accessToken}`
    }

    const tenantId = getResolvedTenantId()
    requestHeaders['X-Tenant-ID'] = tenantId

    // Prepare request options
    const requestOptions: RequestInit = {
        method,
        headers: requestHeaders,
        credentials: 'include'
    }

    // Add body for non-GET requests
    if (body && method !== 'GET') {
        requestOptions.body = JSON.stringify(body)
    }

    // Create abort controller for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)
    requestOptions.signal = controller.signal

    // Determine priority based on endpoint
    let priority: number = REQUEST_PRIORITY.NORMAL
    if (endpoint.includes('/auth/') || endpoint.includes('/login')) {
        priority = REQUEST_PRIORITY.CRITICAL
    } else if (endpoint.includes('/user/') || endpoint.includes('/wallet/')) {
        priority = REQUEST_PRIORITY.HIGH
    }

    // Execute request with rate limiting
    return queueApiRequest(
        async () => {
            try {
                console.log(`🌐 API Request: ${method} ${url}`, {
                    headers: requestHeaders,
                    body: body ? '[REDACTED]' : undefined
                })

                const response = await fetch(url, requestOptions)
                clearTimeout(timeoutId)

                // Parse response
                let responseData
                const contentType = response.headers.get('content-type')

                if (contentType && contentType.includes('application/json')) {
                    responseData = await response.json()
                } else {
                    responseData = await response.text()
                }

                // Handle non-2xx responses
                if (!response.ok) {
                    if (response.status === 401 && responseData?.code === 'SESSION_REPLACED') {
                        await handleSessionReplaced()
                        return
                    }
                    if (shouldHandleAuthExpired(endpoint) && isAuthExpiredResponse(response.status, responseData)) {
                        await handleAuthExpired()
                        const authError = new Error('AUTH_EXPIRED')
                            ; (authError as any).status = response.status
                            ; (authError as any).response = responseData
                            ; (authError as any).code = 'AUTH_EXPIRED'
                        throw authError
                    }
                    const error = new Error(`HTTP ${response.status}: ${response.statusText}`)
                        ; (error as any).status = response.status
                        ; (error as any).response = responseData
                    throw error
                }

                console.log(`✅ API Response: ${method} ${url}`, {
                    status: response.status,
                    success: responseData?.success
                })

                return responseData
            } catch (error: any) {
                clearTimeout(timeoutId)

                if (error.name === 'AbortError') {
                    const timeoutError = new Error('Request timeout')
                        ; (timeoutError as any).status = 408
                    throw timeoutError
                }

                console.error(`❌ API Error: ${method} ${url}`, {
                    status: error.status,
                    message: error.message
                })

                throw error
            }
        },
        priority,
        `${method} ${endpoint}`,
        2 // max retries
    )
}

export default apiRequest
